import * as React from 'react';
import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react';

import type { ClientListItemDto, ClientStateDto, EditClientInput } from '@/api/clients-client';
import {
  useClientsList,
  useCreateClient,
  useEditClient,
  useClientDetail,
  useArchiveClient,
  useReactivateClient,
} from '@/api/use-clients';
import { ClientDetailPanel } from '@/routes/admin/clients/client-detail-panel';
import { useExportsClient } from '@/api/exports-client-context';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/ui/status-badge';
import { clientStateLabel } from '@/lib/client-labels';
import { cn } from '@/lib/utils';

// =============================================================================
// clients-list-screen.tsx — Référentiel Clients bénéficiaires (DBU §2.3, DDD §3.3).
// =============================================================================
// BC client-reference, GLOBAL admin-only, grain SIREN (AR, §3.3.3). Combine la LISTE
// du référentiel (§2.3) ET la CRÉATION d'une fiche Client (admin). La fiche DÉTAILLÉE
// (attributs métier, ContactReferent PII, dossier sociétaire enrichi, chronologie des campagnes
// liées) s'ouvre en VOLET LATÉRAL (client-detail-panel) via l'action « Consulter la fiche » d'une
// ligne — ADDITIVE (S23-CLIENT-FICHE, §2.3 F-C03), superposée à cette liste sans la démonter.
//
// L'AUTORITÉ (RBAC AdminSessionGuard, émission DE.Client*, validité Actif↔Archive,
// unicité SIREN, no-DELETE §3.3.5 inv.3) est l'API. L'UI relaie l'intention.
//
// TRANCHE Uc-A-CLIENTS (DBU §2.3, l.780/808/810/812/814) — FRONT :
//   G1 ORDRE colonnes : « Raison sociale » 1ʳᵉ, puis SIREN, Secteur, État, Date de
//      dernière modification (lastModifiedAt, formatée fr-FR), Auteur de la modification
//      (lastModifiedByLabel, affiché TEL QUEL). (l.780/808)
//   G2 RECHERCHE texte libre : raison sociale OU SIREN (client-side). (l.782/812)
//   G3 FILTRES rapides : statut (Actif/Archivé) + secteur (options dérivées des rows). (l.782/810)
//   G4 TRI client-side sur toutes les colonnes affichées (aria-sort). (l.814)
//   G8 PAGINATION client-side (volume faible ; PAGE_SIZE = 20). (l.814)
// L'état de vue (recherche/filtres/tri/page) vit dans les SEARCH PARAMS d'URL (RULE_REF_006 :
// PAS de localStorage), porté par l'adaptateur routeur (clients-list-route.tsx) via `view`/
// `onChangeView`. Le composant reste testable hors routeur (props contrôlées).
//
// COLONNE « campagnes liées » (G5, R15-CMP-01) : nombre de campagnes liées HISTORIQUEMENT au client,
//   porté par le DTO (`campaignCount`, comptage cross-BC campaign résolu et agrégé côté API en BATCH).
//   Colonne triable (numérique, tabular-nums) ; en-tête mono uppercase via le design system `.ds-table`.
//
// DIFFÉRÉS (NE PAS inventer de données — DDD §3.3, spec §5) :
//   - G7 « auteur de la dernière modification » : porté par le DTO (`lastModifiedByLabel`),
//        affiché TEL QUEL. En mode DÉCOUPLÉ APPROUVÉ, l'API renvoie « — » tant que l'annuaire
//        admin (résolution UUID→nom) n'existe pas (chantier séparé, décision John) : ce « — »
//        est le comportement attendu, PAS un bug. Le front NE résout RIEN (pas de map uuid→nom :
//        aucun UUID auteur n'est exposé — minimisation SECOPS R-5.1). Colonne non triable (valeur
//        dégradée uniforme ; le tri deviendra pertinent quand l'annuaire atterrira).
//   - G9 avertissement archivage campagne Brouillon/Ouverte : donnée campagne absente (S10) ; garde = API.
//   - ContactReferent (nom/email/téléphone) : PII informationnelle (§3.3.5 inv.2), hors read-model liste.
//
// WCAG 2.1 AA : <table> avec <caption>, <th scope> ; en-têtes de tri = <button> + aria-sort ;
// barre d'outils role="search" avec <label> liés ; états rendus en LIBELLÉ texte (badge = renfort
// couleur, jamais seul porteur d'info) ; messages role="status"/role="alert" ; navigation clavier native.

// --- Contrat d'état de vue (porté par l'URL via l'adaptateur routeur) ---------

/** Colonne triable du registre (toutes les colonnes affichées — G4). */
export type ClientSortColumn =
  | 'raisonSociale'
  | 'siren'
  | 'secteurActivite'
  | 'state'
  | 'lastModifiedAt'
  | 'campaignCount';

export type ClientSortDirection = 'asc' | 'desc';

/** Filtre rapide de statut : « all » = tous (défaut implicite, non sérialisé). */
export type ClientStatutFilter = 'all' | ClientStateDto;

/** Colonnes triables valides (garde de désérialisation du search param `tri`). */
export const CLIENT_SORT_COLUMNS: readonly ClientSortColumn[] = [
  'raisonSociale',
  'siren',
  'secteurActivite',
  'state',
  'lastModifiedAt',
  'campaignCount',
];

/** État de VUE du registre (recherche/filtres/tri/page). Vit dans l'URL (RULE_REF_006). */
export interface ClientsRegistryView {
  /** Recherche texte libre (raison sociale OU SIREN) ; '' = aucune. */
  readonly q: string;
  /** Filtre statut ; 'all' = tous. */
  readonly statut: ClientStatutFilter;
  /** Filtre secteur d'activité ; 'all' = tous. */
  readonly secteur: string;
  readonly sortColumn: ClientSortColumn;
  readonly sortDirection: ClientSortDirection;
  /** Page courante (1-based). */
  readonly page: number;
  /**
   * Mode CRÉATION (Bug #3, pattern FOURNISSEUR) : la LISTE est la vue primaire ; le formulaire de
   * création est révélé DERRIÈRE l'action « Nouveau client », jamais ouvert en permanence. Porté par
   * l'URL (search param `nouveau`) comme le reste de la vue — PAS de localStorage (RULE_REF_006).
   */
  readonly creating: boolean;
}

/** Vue par défaut : tout affiché, tri « Raison sociale » ascendant, page 1, création fermée. */
export const DEFAULT_CLIENTS_VIEW: ClientsRegistryView = {
  q: '',
  statut: 'all',
  secteur: 'all',
  sortColumn: 'raisonSociale',
  sortDirection: 'asc',
  page: 1,
  creating: false,
};

/** Taille de page (volume faible côté référentiel ; l.814). */
export const CLIENTS_PAGE_SIZE = 20;

interface ClientsListScreenProps {
  /** État de vue (URL) ; défaut = vue complète non filtrée. */
  readonly view?: ClientsRegistryView;
  /** Notifie le routeur d'un changement de vue (écrit l'état d'URL). */
  readonly onChangeView?: (next: ClientsRegistryView) => void;
}

export function ClientsListScreen({
  view = DEFAULT_CLIENTS_VIEW,
  onChangeView,
}: ClientsListScreenProps = {}): React.JSX.Element {
  const exportsClient = useExportsClient();
  // Bascule du mode création (porté par l'URL via onChangeView — comme le reste de la vue).
  const setCreating = React.useCallback(
    (creating: boolean): void => {
      onChangeView?.({ ...view, creating });
    },
    [onChangeView, view],
  );
  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Référentiel Clients</h1>
          <p className="text-muted-foreground">
            Clients bénéficiaires (hors plateforme). Consultez le référentiel, créez une
            fiche cliente ou modifiez une fiche existante. L'archivage est réversible
            (aucune suppression).
          </p>
        </div>
        {/* Pattern FOURNISSEUR (Bug #3) : la LISTE prime ; la création est une ACTION SECONDAIRE
            explicite (bouton bascule), jamais un formulaire ouvert en permanence. */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => exportsClient.download('clients')}
            aria-label="Télécharger l'export CSV du référentiel clients"
          >
            Exporter en CSV
          </Button>
          <Button
            type="button"
            onClick={() => setCreating(!view.creating)}
            aria-expanded={view.creating}
          >
            Nouveau client
          </Button>
        </div>
      </header>

      {/* Formulaire de création révélé À LA DEMANDE (mode `creating` de l'URL) ; la liste reste
          la vue primaire, montée en permanence en-dessous. */}
      {view.creating ? <CreateClientForm onClose={() => setCreating(false)} /> : null}
      <ClientsRegistry view={view} onChangeView={onChangeView} />
    </div>
  );
}

/**
 * Motif de téléphone TOLÉRÉ (Bug #1, garde-fou UX ; l'autorité de validation reste l'API) : chiffres
 * et séparateurs usuels « + ( ) . - » plus l'espace. Le champ étant facultatif, la chaîne vide est valide.
 */
export const TELEPHONE_PATTERN = /^[+()\d.\s-]*$/u;

/** Motif HTML5 (attribut `pattern`, implicitement ancré) équivalent à TELEPHONE_PATTERN. */
const TELEPHONE_HTML_PATTERN = '[+()\\d.\\s-]*';

/** Vrai si la valeur ne contient que des chiffres et les séparateurs téléphoniques usuels. */
export function isTelephoneValid(value: string): boolean {
  return TELEPHONE_PATTERN.test(value);
}

const SIREN_PATTERN = /^\d{9}$/u;

/** Retourne la chaîne trimée si non-vide, sinon `undefined` (pour un champ facultatif). */
function optional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function CreateClientForm({ onClose }: { readonly onClose: () => void }): React.JSX.Element {
  const create = useCreateClient();
  const [siren, setSiren] = React.useState('');
  const [raisonSociale, setRaisonSociale] = React.useState('');
  const [secteurActivite, setSecteurActivite] = React.useState('');
  // Attributs métier facultatifs.
  const [segment, setSegment] = React.useState('');
  const [taille, setTaille] = React.useState('');
  const [refInterne, setRefInterne] = React.useState('');
  // Contact référent facultatif (PII WRITE-ONLY) : saisi ici, envoyé à l'API, jamais relu par la liste.
  const [contactNom, setContactNom] = React.useState('');
  const [contactFonction, setContactFonction] = React.useState('');
  const [contactEmail, setContactEmail] = React.useState('');
  const [contactTelephone, setContactTelephone] = React.useState('');
  const [created, setCreated] = React.useState<string | null>(null);

  const sirenValid = SIREN_PATTERN.test(siren);
  // Bug #1 — garde-fou UX : le téléphone n'accepte que chiffres + séparateurs usuels (l'API reste l'autorité).
  const telephoneInvalid = !isTelephoneValid(contactTelephone);

  const onSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      // Garde-fou : ne pas soumettre un téléphone malformé (le bouton est aussi désactivé).
      if (!isTelephoneValid(contactTelephone)) {
        return;
      }
      // Narrows locaux (exactOptionalPropertyTypes : une prop présente ne peut valoir `undefined`).
      const segmentV = optional(segment);
      const tailleV = optional(taille);
      const refInterneV = optional(refInterne);
      const nomV = optional(contactNom);
      const fonctionV = optional(contactFonction);
      const emailV = optional(contactEmail);
      const telephoneV = optional(contactTelephone);
      // Contact référent : construit seulement si ≥1 champ non-vide ; sinon absent (aucun contact).
      const contactFields = {
        ...(nomV !== undefined ? { nom: nomV } : {}),
        ...(fonctionV !== undefined ? { fonction: fonctionV } : {}),
        ...(emailV !== undefined ? { email: emailV } : {}),
        ...(telephoneV !== undefined ? { telephone: telephoneV } : {}),
      };
      const hasContact = Object.keys(contactFields).length > 0;
      create.mutate(
        {
          siren,
          raisonSociale,
          secteurActivite,
          ...(segmentV !== undefined ? { segment: segmentV } : {}),
          ...(tailleV !== undefined ? { taille: tailleV } : {}),
          ...(refInterneV !== undefined ? { referenceInterneJeko: refInterneV } : {}),
          ...(hasContact ? { contactReferent: contactFields } : {}),
        },
        {
          onSuccess: ({ siren: createdSiren }) => {
            setCreated(createdSiren);
            setSiren('');
            setRaisonSociale('');
            setSecteurActivite('');
            setSegment('');
            setTaille('');
            setRefInterne('');
            setContactNom('');
            setContactFonction('');
            setContactEmail('');
            setContactTelephone('');
          },
        },
      );
    },
    [
      create,
      siren,
      raisonSociale,
      secteurActivite,
      segment,
      taille,
      refInterne,
      contactNom,
      contactFonction,
      contactEmail,
      contactTelephone,
    ],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Créer une fiche cliente</CardTitle>
        <CardDescription>
          Le client est créé au grain SIREN (société). La raison sociale et le secteur
          d'activité sont enregistrés par l'Admin.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4" aria-label="Créer une fiche cliente">
          <div className="space-y-2">
            <Label htmlFor="client-siren">SIREN (9 chiffres)</Label>
            <Input
              id="client-siren"
              name="siren"
              inputMode="numeric"
              value={siren}
              onChange={(e) => setSiren(e.target.value)}
              aria-describedby="client-siren-help"
              required
            />
            <p id="client-siren-help" className="text-xs text-muted-foreground">
              Identifiant de la société (9 chiffres), distinct du SIRET d'un établissement.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="client-raison-sociale">Raison sociale</Label>
            <Input
              id="client-raison-sociale"
              name="raisonSociale"
              value={raisonSociale}
              onChange={(e) => setRaisonSociale(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="client-secteur">Secteur d'activité</Label>
            <Input
              id="client-secteur"
              name="secteurActivite"
              value={secteurActivite}
              onChange={(e) => setSecteurActivite(e.target.value)}
              required
            />
          </div>

          {/* Attributs métier facultatifs (segment / taille / référence interne recherchable). */}
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="client-segment">Segment (facultatif)</Label>
              <Input
                id="client-segment"
                name="segment"
                value={segment}
                onChange={(e) => setSegment(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="client-taille">Taille (facultatif)</Label>
              <Input
                id="client-taille"
                name="taille"
                value={taille}
                onChange={(e) => setTaille(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="client-ref-interne">Référence interne JEKO (facultatif)</Label>
              <Input
                id="client-ref-interne"
                name="referenceInterneJeko"
                value={refInterne}
                onChange={(e) => setRefInterne(e.target.value)}
              />
            </div>
          </div>

          {/* Contact référent facultatif (PII WRITE-ONLY) : saisi ici, persisté côté API, JAMAIS relu
              par la liste (§3.3.5 inv.2). Aucune contrainte domaine (DBU) : aucun champ requis. */}
          <fieldset className="space-y-4 rounded-lg border border-border p-4">
            <legend className="px-1 text-sm font-medium">Contact référent (facultatif)</legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="contact-nom">Nom</Label>
                <Input
                  id="contact-nom"
                  name="contactNom"
                  value={contactNom}
                  onChange={(e) => setContactNom(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-fonction">Fonction</Label>
                <Input
                  id="contact-fonction"
                  name="contactFonction"
                  value={contactFonction}
                  onChange={(e) => setContactFonction(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-email">Courriel</Label>
                <Input
                  id="contact-email"
                  name="contactEmail"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-telephone">Téléphone</Label>
                <Input
                  id="contact-telephone"
                  name="contactTelephone"
                  type="tel"
                  inputMode="tel"
                  pattern={TELEPHONE_HTML_PATTERN}
                  value={contactTelephone}
                  onChange={(e) => setContactTelephone(e.target.value)}
                  aria-invalid={telephoneInvalid || undefined}
                  aria-describedby={telephoneInvalid ? 'contact-telephone-error' : undefined}
                />
                {telephoneInvalid ? (
                  <p id="contact-telephone-error" role="alert" className="text-sm text-destructive">
                    Le téléphone ne doit contenir que des chiffres et les séparateurs usuels
                    (espace, point, tiret, parenthèses, signe plus).
                  </p>
                ) : null}
              </div>
            </div>
          </fieldset>

          {create.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {create.error.message}
            </p>
          ) : null}
          {created !== null ? (
            <p role="status" className="text-sm text-muted-foreground">
              Fiche cliente créée : SIREN {created}.
            </p>
          ) : null}

          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={
                !sirenValid ||
                raisonSociale.length === 0 ||
                secteurActivite.length === 0 ||
                telephoneInvalid ||
                create.isPending
              }
            >
              {create.isPending ? 'Création…' : 'Créer le client'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// --- Registre : recherche + filtres + tri + pagination (client-side) ----------

/** Formate un horodatage ISO-8601 en date FR lisible (jamais « Invalid Date »). */
function formatDateFr(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

/** Comparateur ascendant d'une ligne sur une colonne (les libellés portent l'ordre affiché). */
function compareClients(
  a: ClientListItemDto,
  b: ClientListItemDto,
  column: ClientSortColumn,
): number {
  switch (column) {
    case 'raisonSociale':
      return a.raisonSociale.localeCompare(b.raisonSociale, 'fr');
    case 'siren':
      return a.siren.localeCompare(b.siren, 'fr');
    case 'secteurActivite':
      return a.secteurActivite.localeCompare(b.secteurActivite, 'fr');
    case 'state':
      return clientStateLabel(a.state).label.localeCompare(clientStateLabel(b.state).label, 'fr');
    case 'lastModifiedAt':
      // ISO 8601 : l'ordre lexicographique coïncide avec l'ordre chronologique.
      return a.lastModifiedAt.localeCompare(b.lastModifiedAt);
    case 'campaignCount':
      // Comparaison numérique (nombre de campagnes liées, R15-CMP-01).
      return a.campaignCount - b.campaignCount;
  }
}

/** Classe partagée des <select> natifs de filtre (tokens sémantiques ; focus visible clavier). */
const SELECT_CLASS = cn(
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
);

function ClientsRegistry({
  view,
  onChangeView,
}: {
  readonly view: ClientsRegistryView;
  readonly onChangeView?: ((next: ClientsRegistryView) => void) | undefined;
}): React.JSX.Element {
  const list = useClientsList();
  const archive = useArchiveClient();
  const reactivate = useReactivateClient();

  // Fiche détail en volet latéral (SIREN sélectionné) — la liste reste montée derrière (R22-FICHE-DRAWER).
  const [selectedSiren, setSelectedSiren] = React.useState<string | null>(null);
  // Édition en volet (S23-CLIENT-EDIT) : SIREN en cours d'édition, ou null. Superposé à la liste.
  const [editingSiren, setEditingSiren] = React.useState<string | null>(null);

  const rows = React.useMemo(() => list.data ?? [], [list.data]);

  // Secteurs DISTINCTS des rows chargées (options du filtre secteur ; dérivées front, sans fetch).
  const secteurOptions = React.useMemo(() => {
    const seen = new Set<string>();
    for (const row of rows) {
      seen.add(row.secteurActivite);
    }
    // Si le filtre actif n'est plus dans les rows, on le garde pour que le <select> reste contrôlé.
    if (view.secteur !== 'all' && !seen.has(view.secteur)) {
      seen.add(view.secteur);
    }
    return [...seen].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [rows, view.secteur]);

  // G2/G3/G4 : recherche + filtres + tri CLIENT-SIDE sur les rows chargées.
  const filteredSorted = React.useMemo(() => {
    const needle = view.q.trim().toLowerCase();
    const filtered = rows.filter((row) => {
      if (view.statut !== 'all' && row.state !== view.statut) {
        return false;
      }
      if (view.secteur !== 'all' && row.secteurActivite !== view.secteur) {
        return false;
      }
      if (needle !== '') {
        // Recherche texte libre : raison sociale OU SIREN OU référence interne JEKO (non-PII, DBU).
        const haystack =
          `${row.raisonSociale} ${row.siren} ${row.referenceInterneJeko ?? ''}`.toLowerCase();
        if (!haystack.includes(needle)) {
          return false;
        }
      }
      return true;
    });
    const sorted = [...filtered].sort((a, b) => {
      const base = compareClients(a, b, view.sortColumn);
      return view.sortDirection === 'asc' ? base : -base;
    });
    return sorted;
  }, [rows, view.q, view.statut, view.secteur, view.sortColumn, view.sortDirection]);

  // G8 : pagination client-side. La page est bornée aux pages existantes.
  const totalPages = Math.max(1, Math.ceil(filteredSorted.length / CLIENTS_PAGE_SIZE));
  const currentPage = Math.min(Math.max(view.page, 1), totalPages);
  const pageRows = filteredSorted.slice(
    (currentPage - 1) * CLIENTS_PAGE_SIZE,
    currentPage * CLIENTS_PAGE_SIZE,
  );

  const emit = React.useCallback(
    (next: ClientsRegistryView): void => {
      onChangeView?.(next);
    },
    [onChangeView],
  );

  const onSort = React.useCallback(
    (column: ClientSortColumn): void => {
      const nextDirection: ClientSortDirection =
        view.sortColumn === column && view.sortDirection === 'asc' ? 'desc' : 'asc';
      emit({ ...view, sortColumn: column, sortDirection: nextDirection });
    },
    [emit, view],
  );

  if (list.isPending) {
    return (
      <p role="status" className="text-muted-foreground">
        Chargement des clients…
      </p>
    );
  }
  if (list.isError) {
    return (
      <p role="alert" className="text-destructive">
        Erreur de chargement : {list.error.message}
      </p>
    );
  }
  if (rows.length === 0) {
    return (
      <p role="status" className="text-muted-foreground">
        Aucun client référencé pour le moment.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* G2/G3 — barre d'outils : recherche + filtres (état d'URL ; filtrage client-side). */}
      <div
        role="search"
        aria-label="Recherche et filtres"
        className="grid gap-4 sm:grid-cols-3"
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="clients-search">Rechercher (raison sociale, SIREN ou réf. interne)</Label>
          <Input
            id="clients-search"
            type="search"
            value={view.q}
            placeholder="Ex. MAIRIE, 552100554 ou JEKO-2026-001"
            onChange={(event) => emit({ ...view, q: event.target.value, page: 1 })}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="clients-filter-statut">Statut</Label>
          <select
            id="clients-filter-statut"
            className={SELECT_CLASS}
            value={view.statut}
            onChange={(event) =>
              emit({
                ...view,
                statut: event.target.value as ClientStatutFilter,
                page: 1,
              })
            }
          >
            <option value="all">Tous les statuts</option>
            <option value="Actif">Actif</option>
            <option value="Archive">Archivé</option>
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="clients-filter-secteur">Secteur d'activité</Label>
          <select
            id="clients-filter-secteur"
            className={SELECT_CLASS}
            value={view.secteur}
            onChange={(event) => emit({ ...view, secteur: event.target.value, page: 1 })}
          >
            <option value="all">Tous les secteurs</option>
            {secteurOptions.map((secteur) => (
              <option key={secteur} value={secteur}>
                {secteur}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* G9 — avertissement contextuel : l'archivage est REFUSÉ (409) si le client est lié à une
          campagne Brouillon/Ouverte (§3.3.5 inv.1). La garde est l'API ; l'UI énonce la règle et
          affiche le refus serveur tel quel. */}
      <p className="text-sm text-muted-foreground">
        L'archivage d'un client lié à une campagne en cours (brouillon ou ouverte) est refusé.
        Clôturez ou détachez ces campagnes au préalable.
      </p>
      {archive.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Archivage refusé : {archive.error.message}
        </p>
      ) : null}

      {filteredSorted.length === 0 ? (
        <p role="status" className="text-muted-foreground">
          Aucun client ne correspond aux critères de recherche.
        </p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="ds-table w-full border-collapse text-sm">
              <caption className="sr-only">
                Liste des clients bénéficiaires référencés : raison sociale, SIREN, secteur
                d'activité, état, date de dernière modification, auteur de la modification et
                nombre de campagnes liées. Les colonnes marquées d'un en-tête bouton sont triables.
              </caption>
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
                  <SortableHeader
                    column="raisonSociale"
                    label="Raison sociale"
                    view={view}
                    onSort={onSort}
                  />
                  <SortableHeader column="siren" label="SIREN" view={view} onSort={onSort} />
                  <SortableHeader
                    column="secteurActivite"
                    label="Secteur d'activité"
                    view={view}
                    onSort={onSort}
                  />
                  <SortableHeader column="state" label="État" view={view} onSort={onSort} />
                  <SortableHeader
                    column="lastModifiedAt"
                    label="Date de dernière modification"
                    view={view}
                    onSort={onSort}
                  />
                  {/* G7 — colonne non triable : valeur dégradée uniforme (« — ») tant que
                      l'annuaire admin n'existe pas. En-tête texte accessible (scope="col"). */}
                  <th scope="col" className="px-4 py-3 font-medium">
                    Auteur de la modification
                  </th>
                  {/* G5 (R15-CMP-01) — colonne triable « Campagnes liées » (comptage cross-BC BATCH). */}
                  <SortableHeader
                    column="campaignCount"
                    label="Campagnes liées"
                    view={view}
                    onSort={onSort}
                  />
                  <th scope="col" className="px-4 py-3 font-medium">
                    <span className="sr-only">Action</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => {
                  const state = clientStateLabel(row.state);
                  return (
                    <tr key={row.siren} className="border-b border-border last:border-b-0">
                      <th scope="row" className="px-4 py-3 text-left align-top font-medium">
                        {row.raisonSociale}
                      </th>
                      <td className="px-4 py-3 align-top font-mono">{row.siren}</td>
                      <td className="px-4 py-3 align-top">{row.secteurActivite}</td>
                      <td className="px-4 py-3 align-top">
                        {/* Badge = renfort ; le LIBELLÉ texte porte l'information. */}
                        <StatusBadge tone={state.tone}>{state.label}</StatusBadge>
                      </td>
                      <td className="px-4 py-3 align-top text-muted-foreground">
                        {formatDateFr(row.lastModifiedAt)}
                      </td>
                      <td className="px-4 py-3 align-top text-muted-foreground">
                        {/* Affiché TEL QUEL : le front ne résout aucun nom (« — » = dégradé approuvé). */}
                        {row.lastModifiedByLabel}
                      </td>
                      {/* G5 (R15-CMP-01) — nombre de campagnes liées, valeur numérique tabular-nums. */}
                      <td className="px-4 py-3 align-top tabular-nums">{row.campaignCount}</td>
                      <td className="px-4 py-3 align-top text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedSiren(row.siren)}
                            aria-label={`Consulter la fiche du client ${row.siren}`}
                          >
                            Consulter la fiche
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingSiren(row.siren)}
                            aria-label={`Éditer la fiche du client ${row.siren}`}
                          >
                            Éditer
                          </Button>
                          {row.state === 'Actif' ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => archive.mutate(row.siren)}
                              aria-label={`Archiver le client ${row.siren}`}
                            >
                              Archiver
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => reactivate.mutate(row.siren)}
                              aria-label={`Réactiver le client ${row.siren}`}
                            >
                              Réactiver
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* G8 — pagination client-side (état d'URL). */}
          <nav
            aria-label="Pagination du registre clients"
            className="flex items-center justify-between gap-4"
          >
            <p role="status" className="text-sm text-muted-foreground">
              Page {currentPage} sur {totalPages} — {filteredSorted.length} client
              {filteredSorted.length > 1 ? 's' : ''}
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => emit({ ...view, page: currentPage - 1 })}
              >
                Précédent
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => emit({ ...view, page: currentPage + 1 })}
              >
                Suivant
              </Button>
            </div>
          </nav>
        </>
      )}

      {/* Formulaire d'édition (S23-CLIENT-EDIT) — superposé, la liste reste montée derrière. */}
      <EditClientForm siren={editingSiren} onClose={() => setEditingSiren(null)} />

      {/* Volet latéral fiche détail (S23-CLIENT-FICHE) — superposé, la liste reste montée derrière. */}
      <ClientDetailPanel siren={selectedSiren} onClose={() => setSelectedSiren(null)} />
    </div>
  );
}

/**
 * Formulaire d'ÉDITION d'une fiche cliente (S23-CLIENT-EDIT, DBU §2.3 l.828). Préremplit depuis la
 * fiche détail (attributs métier), permet d'éditer les attributs NON-NOMINATIFS et, optionnellement,
 * de remplacer le bloc CONTACT RÉFÉRENT (PII WRITE-ONLY) — envoyé seulement si la case dédiée est
 * cochée (parité API : `contactReferent` présent ⇒ `contactReferentChanged`). L'AUTORITÉ (émission
 * DE.ClientEdited, audit avant/après) est l'API ; l'UI relaie l'intention. Rendu conditionnel (siren).
 */
function EditClientForm({
  siren,
  onClose,
}: {
  readonly siren: string | null;
  readonly onClose: () => void;
}): React.JSX.Element | null {
  const detail = useClientDetail(siren);
  const edit = useEditClient();

  const [raisonSociale, setRaisonSociale] = React.useState('');
  const [secteurActivite, setSecteurActivite] = React.useState('');
  const [segment, setSegment] = React.useState('');
  const [taille, setTaille] = React.useState('');
  const [refInterne, setRefInterne] = React.useState('');
  // Édition du bloc contact référent (PII) : opt-in explicite (case à cocher) — parité API.
  const [editContact, setEditContact] = React.useState(false);
  const [contactNom, setContactNom] = React.useState('');
  const [contactFonction, setContactFonction] = React.useState('');
  const [contactEmail, setContactEmail] = React.useState('');
  const [contactTelephone, setContactTelephone] = React.useState('');

  // Préremplissage depuis la fiche détail chargée (attributs métier ; jamais la PII contact par défaut).
  const data = detail.data;
  React.useEffect(() => {
    if (data === undefined) {
      return;
    }
    setRaisonSociale(data.raisonSociale);
    setSecteurActivite(data.secteurActivite);
    setSegment(data.segment ?? '');
    setTaille(data.taille ?? '');
    setRefInterne(data.referenceInterneJeko ?? '');
    setEditContact(false);
    setContactNom('');
    setContactFonction('');
    setContactEmail('');
    setContactTelephone('');
  }, [data]);

  const onSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      if (siren === null) {
        return;
      }
      // Bug #1 — garde-fou : ne pas soumettre un téléphone malformé (le bouton est aussi désactivé).
      if (editContact && !isTelephoneValid(contactTelephone)) {
        return;
      }
      // Attributs nullables : chaîne vide ⇒ effacement (null) ; sinon la valeur saisie.
      const nullable = (value: string): string | null => {
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : null;
      };
      // Narrows locaux (exactOptionalPropertyTypes : une prop présente ne peut valoir `undefined`).
      const nomV = optional(contactNom);
      const fonctionV = optional(contactFonction);
      const emailV = optional(contactEmail);
      const telephoneV = optional(contactTelephone);
      const contactFields = {
        ...(nomV !== undefined ? { nom: nomV } : {}),
        ...(fonctionV !== undefined ? { fonction: fonctionV } : {}),
        ...(emailV !== undefined ? { email: emailV } : {}),
        ...(telephoneV !== undefined ? { telephone: telephoneV } : {}),
      };
      const input: EditClientInput = {
        siren,
        raisonSociale,
        secteurActivite,
        segment: nullable(segment),
        taille: nullable(taille),
        referenceInterneJeko: nullable(refInterne),
        // Contact référent : inclus SEULEMENT si l'admin coche « modifier ». Objet vide ⇒ effacement (null).
        ...(editContact
          ? { contactReferent: Object.keys(contactFields).length > 0 ? contactFields : null }
          : {}),
      };
      edit.mutate(input, { onSuccess: () => onClose() });
    },
    [
      siren,
      raisonSociale,
      secteurActivite,
      segment,
      taille,
      refInterne,
      editContact,
      contactNom,
      contactFonction,
      contactEmail,
      contactTelephone,
      edit,
      onClose,
    ],
  );

  // Bug #1 — le téléphone n'est validé que si le bloc contact est édité (sinon champ vide, valide).
  const telephoneInvalid = editContact && !isTelephoneValid(contactTelephone);

  if (siren === null) {
    return null;
  }

  return (
    <Card aria-label={`Éditer la fiche du client ${siren}`}>
      <CardHeader>
        <CardTitle>Éditer la fiche cliente</CardTitle>
        <CardDescription>
          Modifiez les attributs de la fiche (SIREN {siren}). L'édition est tracée (audit
          avant/après). Le contact référent n'est modifié que si vous le demandez explicitement.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {detail.isPending ? (
          <p role="status" className="text-muted-foreground">
            Chargement de la fiche…
          </p>
        ) : detail.isError ? (
          <p role="alert" className="text-destructive">
            Erreur de chargement : {detail.error.message}
          </p>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4" aria-label="Éditer une fiche cliente">
            <div className="space-y-2">
              <Label htmlFor="edit-raison-sociale">Raison sociale</Label>
              <Input
                id="edit-raison-sociale"
                name="raisonSociale"
                value={raisonSociale}
                onChange={(e) => setRaisonSociale(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-secteur">Secteur d'activité</Label>
              <Input
                id="edit-secteur"
                name="secteurActivite"
                value={secteurActivite}
                onChange={(e) => setSecteurActivite(e.target.value)}
                required
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="edit-segment">Segment</Label>
                <Input
                  id="edit-segment"
                  name="segment"
                  value={segment}
                  onChange={(e) => setSegment(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-taille">Taille</Label>
                <Input
                  id="edit-taille"
                  name="taille"
                  value={taille}
                  onChange={(e) => setTaille(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-ref-interne">Référence interne JEKO</Label>
                <Input
                  id="edit-ref-interne"
                  name="referenceInterneJeko"
                  value={refInterne}
                  onChange={(e) => setRefInterne(e.target.value)}
                />
              </div>
            </div>

            {/* Édition PII opt-in : le bloc contact n'est envoyé QUE si cette case est cochée. */}
            <fieldset className="space-y-4 rounded-lg border border-border p-4">
              <legend className="px-1 text-sm font-medium">Contact référent</legend>
              <div className="flex items-center gap-2">
                <input
                  id="edit-contact-toggle"
                  type="checkbox"
                  checked={editContact}
                  onChange={(e) => setEditContact(e.target.checked)}
                  className="h-4 w-4 rounded border-input"
                />
                <Label htmlFor="edit-contact-toggle" className="font-normal">
                  Modifier le contact référent (laisser vide pour l'effacer)
                </Label>
              </div>
              {editContact ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="edit-contact-nom">Nom</Label>
                    <Input
                      id="edit-contact-nom"
                      name="contactNom"
                      value={contactNom}
                      onChange={(e) => setContactNom(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-contact-fonction">Fonction</Label>
                    <Input
                      id="edit-contact-fonction"
                      name="contactFonction"
                      value={contactFonction}
                      onChange={(e) => setContactFonction(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-contact-email">Courriel</Label>
                    <Input
                      id="edit-contact-email"
                      name="contactEmail"
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-contact-telephone">Téléphone</Label>
                    <Input
                      id="edit-contact-telephone"
                      name="contactTelephone"
                      type="tel"
                      inputMode="tel"
                      pattern={TELEPHONE_HTML_PATTERN}
                      value={contactTelephone}
                      onChange={(e) => setContactTelephone(e.target.value)}
                      aria-invalid={telephoneInvalid || undefined}
                      aria-describedby={
                        telephoneInvalid ? 'edit-contact-telephone-error' : undefined
                      }
                    />
                    {telephoneInvalid ? (
                      <p
                        id="edit-contact-telephone-error"
                        role="alert"
                        className="text-sm text-destructive"
                      >
                        Le téléphone ne doit contenir que des chiffres et les séparateurs
                        usuels (espace, point, tiret, parenthèses, signe plus).
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </fieldset>

            {edit.isError ? (
              <p role="alert" className="text-sm text-destructive">
                {edit.error.message}
              </p>
            ) : null}

            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={
                  raisonSociale.length === 0 ||
                  secteurActivite.length === 0 ||
                  telephoneInvalid ||
                  edit.isPending
                }
              >
                {edit.isPending ? 'Enregistrement…' : 'Enregistrer les modifications'}
              </Button>
              <Button type="button" variant="outline" onClick={onClose}>
                Annuler
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

/** En-tête de colonne triable : <th aria-sort> + <button> déclencheur (clavier natif — G4). */
function SortableHeader({
  column,
  label,
  view,
  onSort,
}: {
  readonly column: ClientSortColumn;
  readonly label: string;
  readonly view: ClientsRegistryView;
  readonly onSort: (column: ClientSortColumn) => void;
}): React.JSX.Element {
  const active = view.sortColumn === column;
  const ariaSort: React.AriaAttributes['aria-sort'] = active
    ? view.sortDirection === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none';
  const Icon = !active ? ChevronsUpDown : view.sortDirection === 'asc' ? ChevronUp : ChevronDown;
  return (
    <th scope="col" aria-sort={ariaSort} className="px-4 py-3 font-medium">
      <button
        type="button"
        onClick={() => onSort(column)}
        className={cn(
          'inline-flex items-center gap-1 rounded-sm font-medium',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        )}
      >
        <span>{label}</span>
        <Icon aria-hidden="true" className="h-3.5 w-3.5" />
      </button>
    </th>
  );
}
